// Side-effect import: MUST stay above the createBlockResolver call below, which
// snapshots the theme registry at module-evaluation time.
import './registerThemes';
import { createBlockResolver, getResolverThemes } from '@akademi/site-builder-runtime';
import type { BlockResolver } from '@akademi/site-builder-runtime';
import { genericBlocks } from '@akademi/site-builder-blocks';
import { commerceBlocks } from '@commerce/site-builder-blocks';
import { PageContentBlock } from './PageContentOutlet';
import { SiteShellBlock } from './SiteShellBlock';
import { hospitalityBlocks } from './hospitality';

/**
 * The storefront's block catalogue.
 *
 * <p>Most of it is `genericBlocks` — the ~140 blocks the platform already ships —
 * and `commerceBlocks`, the shop's own: product listing, facets and detail, basket,
 * checkout, orders, click and collect, and the shopper's account. Those read their
 * data through `CommerceSourceProvider` and `CartProvider`, mounted in
 * `StorefrontAdapterProviders`, and call no `fetch` of their own. This app adds two
 * pieces of synthetic plumbing, rather than anything an operator drags onto a page:
 *
 * <ul>
 *   <li>`PageContent` — the outlet the shell tree renders the route body into;</li>
 *   <li>`SiteShell` — the chrome wrapper the shell's `chrome` slot fills with.</li>
 * </ul>
 *
 * <p>…and the five restaurant blocks from `./hospitality`, which an operator
 * DOES place: `RestaurantHero`, `OpeningHours`, `RestaurantMenu`,
 * `RestaurantOrder`, `RestaurantBooking`. A restaurant tenant is a storefront
 * tenant with those blocks on its pages and a hospitality-capable channel — not
 * a second app. They read `HospitalitySource` through context, mounted in
 * `StorefrontAdapterProviders`, and call no `fetch` of their own.
 *
 * <p>Themes come from the runtime registry rather than a list here.
 *
 * <p><strong>Ordering matters when a theme is added.</strong>
 * `getResolverThemes()` takes a SNAPSHOT of the registry at module-evaluation
 * time, so a theme registered afterwards is silently absent — its blocks fall
 * through to the generic ones with nothing in the console to say why. Adding
 * the Sorsy theme means adding a side-effect `registerThemes.ts` (a
 * `registerTheme(id, bundle)` per theme) and importing it ABOVE the call
 * below, exactly as `apps/lms/portal` does.
 */
export const storefrontBlockResolver: BlockResolver = createBlockResolver({
  defaultBlocks: {
    ...genericBlocks,
    ...commerceBlocks,
    ...hospitalityBlocks,
    PageContent: PageContentBlock,
    SiteShell: SiteShellBlock,
  },
  themes: getResolverThemes(),
});
